var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { GlobalContext } from "@/contexts";
import { getClient } from "@/graphql/ApolloClient";
import { useCountryParam } from "@/hooks/useCountryParam";
import { Box, InputGroup, Input, useSlotRecipe, List } from "@chakra-ui/react";
import React, { useCallback, useContext, useEffect, useId, useRef, useState, } from "react";
import useDetectClickOutside from "../../hooks/useDetectClickOutside";
function AutocompleteInput(_a) {
    var name = _a.name, value = _a.value, placeHolderText = _a.placeHolderText, handleChange = _a.handleChange, handleDropdownItemSelect = _a.handleDropdownItemSelect, id = _a.id, customClass = _a.customClass, error = _a.error, gqlQuery = _a.gqlQuery, icon = _a.icon, rest = __rest(_a, ["name", "value", "placeHolderText", "handleChange", "handleDropdownItemSelect", "id", "customClass", "error", "gqlQuery", "icon"]);
    var _b = useState([]), refs = _b[0], setRefs = _b[1];
    var _c = useState(0), currentSelect = _c[0], setCurrentSelect = _c[1];
    var _d = useState(0), maxListCount = _d[0], setMaxListCount = _d[1];
    var _e = useState(""), selectedValue = _e[0], setSelectedValue = _e[1];
    var _f = useState(false), showDropdown = _f[0], setShowDropdown = _f[1];
    var _g = useState(false), inputFocused = _g[0], setInputFocused = _g[1];
    var _h = useState(null), options = _h[0], setOptions = _h[1];
    var country = useCountryParam();
    var containerRef = useRef(null);
    var dropDownRef = useRef(null);
    var inputRef = useRef(null);
    var recipe = useSlotRecipe({ key: "AutocompleteInput" });
    var recipeProps = recipe.splitVariantProps({})[0];
    var styles = recipe(recipeProps);
    var listId = useId();
    // TODO: Switch autocomplete to "UseQuery"
    var globalContext = useContext(GlobalContext);
    var client = getClient(globalContext.properties.graphQLUri, globalContext.properties.placementId);
    var setSelectedValueToStateAndResetOptions = function (selectedItem) {
        setSelectedValue(selectedItem);
        handleDropdownItemSelect(selectedItem);
        setOptions(null);
        setRefs([]);
    };
    var setAndFocusCurrentSelect = function (index) {
        var _a;
        var ref = refs[index];
        (_a = ref.current) === null || _a === void 0 ? void 0 : _a.focus();
        setCurrentSelect(index);
    };
    var mod = function (dividend, divisor) {
        return ((dividend % divisor) + divisor) % divisor;
    };
    var handleInputKeyDown = function (e) {
        var _a, _b;
        if (options === null || options === void 0 ? void 0 : options.length) {
            if (e.key === "ArrowDown") {
                e.preventDefault();
                setAndFocusCurrentSelect(0);
                setShowDropdown(true);
            }
            if (e.key === "ArrowUp") {
                e.preventDefault();
                setAndFocusCurrentSelect(maxListCount - 1);
                setShowDropdown(true);
            }
            if (e.key === "Escape") {
                e.preventDefault();
                (_a = inputRef.current) === null || _a === void 0 ? void 0 : _a.focus();
                setShowDropdown(false);
            }
            if (e.key === "Enter") {
                (_b = inputRef.current) === null || _b === void 0 ? void 0 : _b.focus();
                setShowDropdown(false);
            }
        }
    };
    var handleDropDownItemKeyDown = function (e) {
        var _a, _b;
        if (e.key !== "Tab") {
            e.preventDefault();
        }
        if (e.key === "ArrowDown") {
            var index = mod(currentSelect + 1, maxListCount);
            setAndFocusCurrentSelect(index);
        }
        if (e.key === "ArrowUp") {
            var index = mod(currentSelect - 1, maxListCount);
            setAndFocusCurrentSelect(index);
        }
        if (e.key === "Enter") {
            if (e.currentTarget.textContent)
                setSelectedValueToStateAndResetOptions(e.currentTarget.textContent);
            (_a = inputRef.current) === null || _a === void 0 ? void 0 : _a.focus();
            setShowDropdown(false);
        }
        if (e.key === "Escape") {
            e.preventDefault();
            (_b = inputRef.current) === null || _b === void 0 ? void 0 : _b.focus();
            setShowDropdown(false);
        }
    };
    var handleListItemClick = function (e) {
        e.preventDefault();
        var listItem = e.currentTarget.textContent;
        if (listItem) {
            setSelectedValueToStateAndResetOptions(listItem);
            setShowDropdown(false);
        }
    };
    var handleInputChange = function (e) {
        if (e.target.value === "" ||
            (!e.target.value.includes(value, 0) && !value.includes(e.target.value, 0))) {
            setOptions(null);
        } // reset options if the input value is empty or the new value is not a substring of the old value and vice versa
        handleChange(e);
        setCurrentSelect(0);
    };
    var handleFetchDropdown = useCallback(function () {
        if (gqlQuery === undefined)
            return; // autocomplete is disabled for this input
        if (value === "")
            return; // no need to fetch if input is empty
        gqlQuery(client, value, country)
            .then(function (options) { return setOptions(options); })
            .catch(function () { });
    }, [value]);
    useEffect(function () {
        if (inputFocused && value !== selectedValue) {
            handleFetchDropdown();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [value, handleFetchDropdown, selectedValue]);
    useEffect(function () {
        setOptions(null);
    }, [country]);
    useEffect(function () {
        if (options && options instanceof Array && inputFocused) {
            setMaxListCount(options.length);
            setShowDropdown(!!value);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [options, value]);
    useEffect(function () {
        var _a, _b, _c;
        function handleFocusOutside(event) {
            var _a;
            if (!((_a = containerRef.current) === null || _a === void 0 ? void 0 : _a.contains(event.target))) {
                setShowDropdown(false);
            }
        }
        var ownerDocument = (_b = (_a = containerRef.current) === null || _a === void 0 ? void 0 : _a.ownerDocument) !== null && _b !== void 0 ? _b : document;
        var rootNode = (_c = containerRef.current) === null || _c === void 0 ? void 0 : _c.getRootNode();
        var eventTarget = rootNode && rootNode !== ownerDocument ? rootNode : ownerDocument;
        eventTarget.addEventListener("focusin", handleFocusOutside);
        return function () {
            eventTarget.removeEventListener("focusin", handleFocusOutside);
        };
    }, [containerRef]);
    useDetectClickOutside(dropDownRef, setShowDropdown);
    if (showDropdown && options instanceof Array && options.length > 0) {
        rest["aria-controls"] = listId;
    }
    return (_jsxs(Box, __assign({ css: styles.container, position: "relative", className: customClass, ref: containerRef }, { children: [_jsx(InputGroup, __assign({ css: { width: "100%" }, startElement: icon }, { children: _jsx(Input, { css: styles.input, onChange: handleInputChange, onKeyDown: handleInputKeyDown, onFocus: function () { return setInputFocused(true); }, onBlur: function () { return setInputFocused(false); }, value: value, ref: inputRef, name: name, type: "text", autoComplete: "off", id: id, className: "".concat(error ? "error" : ""), role: "combobox", "aria-expanded": showDropdown && options instanceof Array && options.length > 0, placeholder: placeHolderText }) })), showDropdown && options instanceof Array && options.length > 0 && (_jsx(List.Root, __assign({ css: styles.list, listStyleType: "none", marginLeft: 0, position: "absolute", alignItems: "flex-start", ref: dropDownRef, role: "listbox", backgroundColor: "white", zIndex: "dropdown", width: "100%", id: listId }, { children: options.length
                    ? options.map(function (item, index) {
                        var newRef = null;
                        if (refs.length <= index) {
                            newRef = React.createRef();
                            setRefs(function (oldArray) { return __spreadArray(__spreadArray([], oldArray, true), [newRef], false); });
                        }
                        else {
                            newRef = refs[index];
                        }
                        return (_jsx(List.Item, __assign({ css: styles.listItem, ref: newRef, onClick: handleListItemClick, onKeyDown: handleDropDownItemKeyDown, role: "option", "aria-posinset": index, "aria-setsize": options.length, tabIndex: -1 }, { children: item }), item));
                    })
                    : "Loading..." })))] })));
}
export { AutocompleteInput };
